import React, { useEffect, useRef, useState } from 'react';
import { sopFire } from '../../../utils/sopSwal';
import StageChrome from '../components/StageChrome';
import useGameTimer from '../components/useGameTimer';
import { ASSET, reportOnSubmit, stagecheck, submitStage } from '../../../utils/sopGame';
import '../stages.css';

/**
 * Crossword layout (letter or null empty).
 * User-editable cells keyed by field id; given letters as plain string.
 */
const EXPECTED = {
  ans1: 'V',
  ans2: 'E',
  ans3: 'R',
  ans4: 'R',
  ans5: 'U',
  ans6: 'P',
  ans7: 'T',
  ans8: 'A',
};

/** Typing order for auto-advance */
const INPUT_ORDER = ['ans1', 'ans2', 'ans3', 'ans4', 'ans5', 'ans6', 'ans7', 'ans8'];

// PHP layout (top → bottom): K, I, VEERARJUN, PRATAP, I
// KIRTI vertical at column 3: K, I, ans3(R), ans7(T), I
const ROWS = [
  [
    { type: 'empty' },
    { type: 'empty' },
    { type: 'empty' },
    { type: 'fixed', ch: 'K' },
    { type: 'empty' },
    { type: 'empty' },
    { type: 'empty' },
    { type: 'empty' },
    { type: 'empty' },
  ],
  [
    { type: 'empty' },
    { type: 'empty' },
    { type: 'empty' },
    { type: 'fixed', ch: 'I' },
    { type: 'empty' },
    { type: 'empty' },
    { type: 'empty' },
    { type: 'empty' },
    { type: 'empty' },
  ],
  [
    { type: 'input', id: 'ans1' },
    { type: 'input', id: 'ans2' },
    { type: 'fixed', ch: 'E' },
    { type: 'input', id: 'ans3' },
    { type: 'fixed', ch: 'A' },
    { type: 'input', id: 'ans4' },
    { type: 'fixed', ch: 'J' },
    { type: 'input', id: 'ans5' },
    { type: 'fixed', ch: 'N' },
  ],
  [
    { type: 'input', id: 'ans6' },
    { type: 'fixed', ch: 'R' },
    { type: 'fixed', ch: 'A' },
    { type: 'input', id: 'ans7' },
    { type: 'input', id: 'ans8' },
    { type: 'fixed', ch: 'P' },
    { type: 'empty' },
    { type: 'empty' },
    { type: 'empty' },
  ],
  [
    { type: 'empty' },
    { type: 'empty' },
    { type: 'empty' },
    { type: 'fixed', ch: 'I' },
    { type: 'empty' },
    { type: 'empty' },
    { type: 'empty' },
    { type: 'empty' },
    { type: 'empty' },
  ],
];

export default function Stage4({ user, onComplete, reportId, initialSeconds }) {
  const [vals, setVals] = useState({
    ans1: '',
    ans2: '',
    ans3: '',
    ans4: '',
    ans5: '',
    ans6: '',
    ans7: '',
    ans8: '',
  });
  const [cls, setCls] = useState({});
  const [score, setScore] = useState(0);
  const [sending, setSending] = useState(false);
  const awarded = useRef({ kirti: false, veer: false, pratap: false });
  const inputRefs = useRef({});
  const timeeRef = useRef('00:00:00');
  const { display } = useGameTimer(initialSeconds, (hms) => {
    timeeRef.current = hms;
  });

  useEffect(() => {
    stagecheck(user).then(setScore).catch(console.error);
  }, [user]);

  const focusNext = (id) => {
    const i = INPUT_ORDER.indexOf(id);
    for (let j = i + 1; j < INPUT_ORDER.length; j += 1) {
      const nextId = INPUT_ORDER[j];
      const el = inputRefs.current[nextId];
      if (el && !el.readOnly) {
        el.focus();
        el.select?.();
        return;
      }
    }
  };

  const focusPrev = (id) => {
    const i = INPUT_ORDER.indexOf(id);
    for (let j = i - 1; j >= 0; j -= 1) {
      const prevId = INPUT_ORDER[j];
      const el = inputRefs.current[prevId];
      if (el && !el.readOnly) {
        el.focus();
        el.select?.();
        return;
      }
    }
  };

  const setLetter = (id, raw) => {
    const ch = String(raw || '')
      .slice(-1)
      .toUpperCase();
    setVals((prev) => {
      const next = { ...prev, [id]: ch };
      return next;
    });
    setCls((c) => ({
      ...c,
      [id]: !ch ? '' : ch === EXPECTED[id] ? 'correct' : 'wrong',
    }));

    const next = { ...vals, [id]: ch };
    const g = (k) => next[k] || '';
    if (g('ans3') === 'R' && g('ans7') === 'T' && !awarded.current.kirti) {
      awarded.current.kirti = true;
      setScore((s) => s + 1);
      sopFire({ text: 'Kirti — a magazine associated with Bhagat Singh' });
    }
    if (
      g('ans1') === 'V' &&
      g('ans2') === 'E' &&
      g('ans3') === 'R' &&
      g('ans4') === 'R' &&
      g('ans5') === 'U' &&
      !awarded.current.veer
    ) {
      awarded.current.veer = true;
      setScore((s) => s + 1);
      sopFire({ text: 'Veerarjun — a newspaper' });
    }
    if (g('ans6') === 'P' && g('ans7') === 'T' && g('ans8') === 'A' && !awarded.current.pratap) {
      awarded.current.pratap = true;
      setScore((s) => s + 1);
      sopFire({ text: 'Pratap — a newspaper' });
    }

    if (ch) {
      // Defer focus so React can apply readOnly on correct cells first
      requestAnimationFrame(() => focusNext(id));
    }
  };

  const onKeyDown = (e, id) => {
    if (e.key === 'Backspace' && !vals[id]) {
      e.preventDefault();
      focusPrev(id);
      return;
    }
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      focusNext(id);
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      focusPrev(id);
    }
  };

  const submit = async () => {
    if (sending) return;
    if (Object.values(vals).some((v) => !v)) {
      sopFire({ text: 'Please fill all fields before submitting.' });
      return;
    }
    const w1 = (`KI${vals.ans3}${vals.ans7}I`).toLowerCase();
    const w2 = (`${vals.ans1}${vals.ans2}E${vals.ans3}A${vals.ans4}J${vals.ans5}N`).toLowerCase();
    const w3 = (`${vals.ans6}RA${vals.ans7}${vals.ans8}P`).toLowerCase();
    let points1 = 0;
    if (w1 === 'kirti') points1 += 1;
    if (w2 === 'veerarjun') points1 += 1;
    if (w3 === 'pratap') points1 += 1;

    setSending(true);
    try {
      await submitStage(user, {
        points: score,
        points1,
        round: 4,
        timee: timeeRef.current,
        reportId,
      });
      await reportOnSubmit(user, { points: score, timee: timeeRef.current, round: 4, reportId });
      const msg =
        points1 >= 1
          ? 'Great work on the crossword! Proceed.'
          : 'Proceed to the next stage.';
      await sopFire({ text: msg });
      onComplete();
    } catch (err) {
      console.error(err);
      sopFire({ text: err.message || 'Submit failed' });
      setSending(false);
    }
  };

  return (
    <StageChrome
      title="Bhagat Singh, one of India's most revered freedom fighters, made significant contributions to the country's struggle for independence."
      score={score}
      timerDisplay={display}
    >
      <p className="sop-subtitle">
        Few know that he was an avid writer too and contributed several articles to spread awareness
        and inspire others to join the struggle for freedom. Identify a few names of media that he
        contributed towards:
      </p>
      <div className="sop-crossword">
        {ROWS.map((row, ri) => (
          <div className="sop-cw-row" key={ri}>
            {row.map((cell, ci) => {
              if (cell.type === 'empty') {
                return <div className="sop-cw-cell empty" key={`${ri}-${ci}`} />;
              }
              if (cell.type === 'fixed') {
                return (
                  <div className="sop-cw-cell" key={`${ri}-${ci}`}>
                    <input readOnly value={cell.ch} />
                  </div>
                );
              }
              return (
                <div className="sop-cw-cell" key={`${ri}-${ci}`}>
                  <input
                    ref={(el) => {
                      inputRefs.current[cell.id] = el;
                    }}
                    className={cls[cell.id] || ''}
                    maxLength={1}
                    value={vals[cell.id]}
                    readOnly={cls[cell.id] === 'correct'}
                    onChange={(e) => setLetter(cell.id, e.target.value)}
                    onKeyDown={(e) => onKeyDown(e, cell.id)}
                    autoComplete="off"
                    inputMode="text"
                  />
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div className="sop-actions">
        <button type="button" className="sop-img-btn" disabled={sending} onClick={submit}>
          <img src={ASSET.img('submit.png')} alt="Submit" />
        </button>
      </div>
    </StageChrome>
  );
}
